
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, FlaskConical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ChemicalSearchCombobox } from '@/components/portfolio/ChemicalSearchCombobox';
import { Chemical } from '@/hooks/useChemicalSearch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Material {
  id: string;
  name: string;
  type: string;
  cas_number?: string | null;
}

interface FormulaMaterialStepProps {
  formData: {
    materials: Material[];
  };
  updateFormData: (data: { materials: Material[] }) => void;
}

export const FormulaMaterialStep = ({ formData, updateFormData }: FormulaMaterialStepProps) => {
  const [newMaterial, setNewMaterial] = useState('');
  const [selectedType, setSelectedType] = useState('raw');
  const [isChemicalSearch, setIsChemicalSearch] = useState(false);
  
  const handleAddMaterial = () => {
    if (!newMaterial.trim() && !isChemicalSearch) return;
    
    const material = {
      id: Date.now().toString(),
      name: newMaterial,
      type: selectedType,
    };
    
    updateFormData({
      materials: [...formData.materials, material],
    });
    
    setNewMaterial('');
  };

  const handleChemicalSelect = (chemical: Chemical) => {
    const material = {
      id: Date.now().toString(),
      name: chemical.name,
      type: selectedType,
      cas_number: chemical.cas_number || undefined
    };
    
    updateFormData({
      materials: [...formData.materials, material],
    });
  };
  
  const handleRemoveMaterial = (id: string) => {
    updateFormData({
      materials: formData.materials.filter(material => material.id !== id),
    });
  };

  const toggleInputMode = () => {
    setIsChemicalSearch(!isChemicalSearch);
    setNewMaterial('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Add Materials</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Specify the raw materials and chemical components used in your formula.
        </p>
      </div>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center mb-4">
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={toggleInputMode}
              className="mr-2"
            >
              {isChemicalSearch ? "Manual Entry" : "Search Chemicals"}
            </Button>
            <p className="text-sm text-muted-foreground">
              {isChemicalSearch 
                ? "Search for chemicals in the database" 
                : "Manually enter material names"}
            </p>
          </div>

          <div className="flex space-x-3 mb-4">
            <div className="flex-1">
              {isChemicalSearch ? (
                <ChemicalSearchCombobox onSelect={handleChemicalSelect} />
              ) : (
                <Input
                  placeholder="Enter material name"
                  value={newMaterial}
                  onChange={(e) => setNewMaterial(e.target.value)}
                />
              )}
            </div>
            <div>
              <Select 
                value={selectedType}
                onValueChange={(value) => setSelectedType(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="raw">Raw Material</SelectItem>
                  <SelectItem value="chemical">Chemical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isChemicalSearch && (
              <div>
                <Button onClick={handleAddMaterial}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {formData.materials.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Material Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>CAS Number</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {formData.materials.map((material) => (
              <TableRow key={material.id}>
                <TableCell className="font-medium">{material.name}</TableCell>
                <TableCell>
                  <Badge variant={material.type === 'raw' ? 'outline' : 'secondary'}>
                    {material.type === 'raw' ? 'Raw Material' : 'Chemical'}
                  </Badge>
                </TableCell>
                <TableCell>{material.cas_number || '-'}</TableCell>
                <TableCell>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleRemoveMaterial(material.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center p-4 border rounded-md bg-muted/30">
          <FlaskConical className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">No materials added yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Add materials using the form above</p>
        </div>
      )}
    </div>
  );
};
